import { apiPost, buildFormData } from '../api.utils'
import type { IResponseCommon } from '../common.types'
import type { AxiosProgressEvent } from 'axios'

type NormalizedImage = { name: string; base64: string; mimeType: 'image/jpeg'; size: number }
type NormalizationResponse = IResponseCommon & { data?: { converted: boolean; files?: NormalizedImage[] } }
export type ListenerUploadProgress = { stage: 'uploading' | 'processing' | 'downloading'; percent?: number }

const invalidResponse = () => new Error('Сервер вернул некорректный результат подготовки файла.')

/** Detect images by their contents on the server, including unknown extensions. */
export const normalizeListenerUploadFile = async (file: File, signal?: AbortSignal, onProgress?: (value: ListenerUploadProgress) => void): Promise<File[]> => {
    if (signal?.aborted) throw new DOMException('Запрос отменён', 'AbortError')
    let active = true, stage = -1
    let lastEvent: string | undefined
    const lastPercent: Partial<Record<ListenerUploadProgress['stage'], number>> = {}
    const report = (value: ListenerUploadProgress) => {
        if (!onProgress || !active || signal?.aborted) return
        const nextStage = { uploading: 0, processing: 1, downloading: 2 }[value.stage]
        if (nextStage < stage) return
        stage = nextStage
        // A late transport event must not move either a stage or its byte
        // percentage backwards. Unknown totals remain indeterminate.
        const percent = value.percent === undefined ? undefined : Math.max(lastPercent[value.stage] ?? 0, value.percent)
        if (percent !== undefined) lastPercent[value.stage] = percent
        const key = `${value.stage}:${percent ?? ''}`
        if (key === lastEvent) return
        lastEvent = key
        onProgress({ stage: value.stage, ...(percent === undefined ? {} : { percent }) })
    }
    const transferPercent = ({ loaded, total }: AxiosProgressEvent) => Number.isFinite(loaded) && loaded >= 0
        && typeof total === 'number' && Number.isFinite(total) && total > 0
        ? Math.max(0, Math.min(100, Math.floor(loaded / total * 100))) : undefined
    const progressConfig = onProgress ? {
        onUploadProgress: (event: AxiosProgressEvent) => {
            const percent = transferPercent(event)
            report(percent === 100 ? { stage: 'processing' } : { stage: 'uploading', ...(percent === undefined ? {} : { percent }) })
        },
        onDownloadProgress: (event: AxiosProgressEvent) => {
            const percent = transferPercent(event)
            report({ stage: 'downloading', ...(percent === undefined ? {} : { percent }) })
        },
    } : {}
    report({ stage: 'uploading' })
    let response: NormalizationResponse
    try {
        response = await apiPost<NormalizationResponse>('/listeners/normalizeUploadFile',
            { signal, timeout: 10 * 60 * 1000, ...progressConfig }, buildFormData({ docs: [file] }))
    } finally { active = false }
    if (signal?.aborted) throw new DOMException('Запрос отменён', 'AbortError')
    if (response.status !== 200 || !response.data || typeof response.data.converted !== 'boolean') {
        throw new Error(response.message || 'Не удалось подготовить файл. Повторите загрузку.')
    }
    if (!response.data.converted) {
        if (response.data.files !== undefined && (!Array.isArray(response.data.files) || response.data.files.length)) throw invalidResponse()
        return [file]
    }
    const images = response.data.files
    if (!Array.isArray(images) || !images.length) throw invalidResponse()
    return images.map(image => {
        if (!image || typeof image.name !== 'string' || !image.name.trim() || image.name !== image.name.trim()
            || !/\.jpg$/i.test(image.name) || /[\\/\u0000-\u001f\u007f]/.test(image.name)
            || image.mimeType !== 'image/jpeg' || !Number.isSafeInteger(image.size) || image.size <= 0
            || typeof image.base64 !== 'string' || !image.base64.length) throw invalidResponse()
        let binary: string
        try {
            binary = atob(image.base64)
            if (btoa(binary) !== image.base64) throw invalidResponse()
        } catch { throw invalidResponse() }
        if (binary.length !== image.size || binary.charCodeAt(0) !== 0xff || binary.charCodeAt(1) !== 0xd8 || binary.charCodeAt(2) !== 0xff) throw invalidResponse()
        const bytes = new Uint8Array(binary.length)
        for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index)
        return new File([bytes], image.name, { type: image.mimeType, lastModified: file.lastModified })
    })
}
